"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { useAutocomplete } from "@server/hooks";
import { useQueryClient } from "@tanstack/react-query";
import debounce from "lodash.debounce";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { ChangeEvent, ComponentProps, InputHTMLAttributes } from "react";
import { Suspense, useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { useMenuStore } from "@/stores/menuStore";
import { useSearchStore } from "@/stores/searchStore";

import getImagesFromProduct from "@/utils/elevate/getImageInfoFromProduct";
import getPriceInfoFromProduct from "@/utils/elevate/getPriceInfoFromProduct";
import makeSlug from "@/utils/makeSlug";
import { formatPrice } from "@/utils/priceUtils";
import { usePageCatalog } from "@app/_components/PageCatalog/PageCatalogReader";
import { useMarket } from "@app/_context";
import useEvents from "@app/_hooks/useEvents";
import type { ProductGroup } from "@apptus/esales-api";
import { Market } from "@features/market/config/i18n-config";
import { findMarketByLocale } from "@features/market/config/market-config";
import createHrefWithQuery from "@features/search/utils/createHrefWithQuery";
import Image from "next/image";
import { Search } from "../Search";

function SuggestedProduct({
  productGroup,
  market,
  query,
  dataType,
}: {
  productGroup: ProductGroup;
  market: Market;
  query: string;
  dataType: string;
}) {
  const [product] = productGroup.products;
  const [variant] = product?.variants ?? [];
  if (!product || !variant?.link) return null;
  const [image] = getImagesFromProduct(product);
  const price = getPriceInfoFromProduct(product, market);

  return (
    <Search.Suggestion
      className="grid grid-flow-col grid-cols-[auto_1fr] gap-x-2 overflow-hidden p-0 not-last:border-b"
      value={variant.link}
      data-type={dataType}
      data-ticket={variant.ticket}
    >
      {image?.src && (
        <figure className="bg-primary relative flex h-full w-14 shrink-0 items-center">
          <Image
            src={image.src}
            width={60}
            height={60}
            alt={image.alt ?? product.title}
            className="h-full w-full object-contain mix-blend-multiply"
          />
        </figure>
      )}
      <div className="flex grow flex-col items-start justify-start overflow-hidden py-1">
        <span className="w-full overflow-auto text-ellipsis whitespace-nowrap uppercase">
          {product.brand}
        </span>
        <span className="w-full overflow-auto text-base text-ellipsis whitespace-nowrap">
          {getStylizedText(product.title, query)}
        </span>
        <span>{formatPrice(price.price ?? 0, market.locale)}</span>
      </div>
    </Search.Suggestion>
  );
}

function exactContentPath(
  lists:
    | { items: { title?: string | null; link?: string | null }[] }[]
    | undefined,
  query: string,
) {
  const needle = query.trim().toLowerCase();
  if (!needle) return;

  for (const list of lists ?? []) {
    const match = list.items.find(
      (item) => item.link && item.title?.trim().toLowerCase() === needle,
    );
    if (match?.link) return match.link;
  }
}

const getStylizedText = (text = "", query = "") => {
  if (!query) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const highlight = text.replace(
    new RegExp(escaped, "i"),
    (match: string) => `***${match}***`,
  );
  const [prefix, match, suffix] = highlight.split("***");
  return (
    <>
      <span className="pointer-events-none">{prefix}</span>
      <span className="pointer-events-none">{match}</span>
      <span className="pointer-events-none">{suffix}</span>
    </>
  );
};

const HeaderSearch = ({
  searchInputId,
  classNames,
  onBlur: blurHandler = () => null,
  suggestions: showSuggestions = true,
}: {
  searchInputId: string;
  suggestions?: boolean;
  classNames?: {
    root?: string;
    input?: string;
    icon?: string;
  };
} & InputHTMLAttributes<HTMLInputElement>) => {
  const t = useTranslate();
  const pages = usePageCatalog();
  const { trackTicket } = useEvents();
  const { locale } = useMarket().state.market;
  const market = findMarketByLocale(locale);

  const pathname = usePathname();
  const router = useRouter();
  const params = useSearchParams();

  const queryClient = useQueryClient();

  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);
  const setSearchFocus = useSearchStore((state) => state.setSearchFocus);

  const [searchValue, setSearchValue] = useState(params.get("query") || "");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const searchQuery = useSearchStore((state) => state.searchQuery);
  const setSearchQuery = useSearchStore((state) => state.setSearchQuery);

  const autocomplete = useAutocomplete({
    query: searchQuery,
    enabled: suggestionsOpen,
  });

  const debouncedSetSearchQuery = useMemo(
    () => debounce(setSearchQuery, 300),
    [setSearchQuery],
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    debouncedSetSearchQuery(event.target.value);
  };

  const onSearch = ({
    value,
    dataType,
    dataTicket,
  }: {
    value: string;
    dataType: string | null;
    dataTicket?: string | null;
  }) => {
    debouncedSetSearchQuery.cancel();
    /* The above should in theory be enough, but we cancel the query either way in case network is slow */
    void queryClient.cancelQueries({
      queryKey: ["autocomplete"],
    });
    void queryClient.invalidateQueries({
      queryKey: ["autocomplete"],
    });

    if (dataTicket) {
      void trackTicket(dataTicket);
    }

    setMobileMenuOpen(false);
    setSearchFocus(false);
    setSuggestionsOpen(false);
    setSearchQuery("");

    if (value.startsWith("/")) {
      router.push(makeSlug(market.siteLocale, value));
      return;
    }

    if (!dataType) {
      const contentPath = exactContentPath(
        autocomplete.data?.contentLists,
        value,
      );
      if (contentPath) {
        router.push(
          contentPath.startsWith("/")
            ? makeSlug(market.siteLocale, contentPath)
            : contentPath,
        );
        return;
      }

      const normalized = value.trim();
      const page =
        pages?.get(normalized) ?? pages?.get(normalized.toLowerCase());
      if (page?.slug) {
        router.push(makeSlug(market.siteLocale, page.slug));
        return;
      }
    }

    const origin =
      dataType === "suggested-phrase"
        ? "PHRASE_SUGGEST"
        : dataType === "recent-search"
          ? "RECENT_SEARCH"
          : "ORGANIC";

    router.push(
      createHrefWithQuery(makeSlug(market.siteLocale, market.searchPathName), {
        query: value,
        origin,
      }),
    );
  };

  useEffect(() => {
    if (pathname.includes(market.searchPathName)) {
      setSearchValue(params.get("query") || "");
    } else {
      setSearchValue("");
    }
    setSearchFocus(false);
  }, [params, pathname, market, setSearchValue, setSearchFocus]);

  return (
    <Search.Root
      onSearch={onSearch}
      className={cn("static w-full lg:relative", classNames?.root)}
    >
      <Search.Input
        id={searchInputId}
        data-testid="search-input"
        value={searchValue}
        onBlur={(event) => {
          setSuggestionsOpen(false);
          blurHandler(event);
        }}
        onFocus={() => setSuggestionsOpen(true)}
        onChange={handleChange}
        placeholder={t("searchTerm")}
        classNames={classNames}
      />
      {showSuggestions && autocomplete.data ? (
        <>
          {searchQuery.length > 0 ? (
            <Search.List className="border-gray-150 max-h-[calc(100dvh-5rem)] w-full overflow-x-hidden overflow-y-auto border-t-0 bg-white text-sm lg:max-h-[calc(100dvh-7.5rem)] lg:border lg:border-t-0">
              {autocomplete.data.phraseSuggestions.length > 0 && (
                <section className="border-b py-1">
                  <h6 className="mb-3 px-9 py-1 whitespace-nowrap uppercase">
                    {t("searchResults")}
                  </h6>
                  {autocomplete.data.phraseSuggestions.map((item) => (
                    <Search.Suggestion
                      className="px-9 py-1"
                      key={item.q}
                      value={item.q}
                      data-type="suggested-phrase"
                    >
                      {getStylizedText(item.q, searchQuery)}
                    </Search.Suggestion>
                  ))}
                </section>
              )}
              {autocomplete.data.contentLists.map((contentList) => {
                if (contentList.totalHits === 0) return null;

                const translationKey =
                  contentList.id === "brands"
                    ? "brands"
                    : contentList.id === "designers"
                      ? "designers"
                      : contentList.id === "categories"
                        ? "categories"
                        : "searchResults";

                return (
                  <section className="border-b py-1" key={contentList.id}>
                    <h6 className="px-9 py-1 whitespace-nowrap uppercase">
                      {t(translationKey)}
                    </h6>
                    {contentList.items.map((item) => (
                      <Search.Suggestion
                        className="px-9 py-1"
                        key={item.key}
                        value={item.link}
                        data-type="suggested-page"
                      >
                        {getStylizedText(item.title, searchQuery)}
                      </Search.Suggestion>
                    ))}
                  </section>
                );
              })}
              {autocomplete.data.productSuggestions.length > 0 && (
                <section className="border-b py-1">
                  <h6 className="mb-3 px-9 py-1 whitespace-nowrap uppercase">
                    {t("products")}
                  </h6>
                  {autocomplete.data.productSuggestions.map((item) => (
                    <SuggestedProduct
                      key={item.key}
                      productGroup={item}
                      market={market}
                      query={searchQuery}
                      dataType="suggested-product"
                    />
                  ))}
                </section>
              )}
            </Search.List>
          ) : (
            <>
              <Search.List className="border-gray-150 max-h-[calc(100dvh-5rem)] w-full overflow-x-hidden overflow-y-auto border-t-0 bg-white text-sm lg:max-h-[calc(100dvh-7.5rem)] lg:border lg:border-t-0">
                {autocomplete.data.recentSearches.length > 0 && (
                  <section className="border-b py-1">
                    <h6 className="mb-3 px-9 py-1 whitespace-nowrap uppercase">
                      {t("previousSearches")}
                    </h6>
                    {autocomplete.data.recentSearches.map((item) => (
                      <Search.Suggestion
                        className="px-9 py-1"
                        key={item.q}
                        value={item.q}
                        data-type="recent-search"
                      >
                        {item.q}
                      </Search.Suggestion>
                    ))}
                  </section>
                )}
                {autocomplete.data.recentlyViewed.length > 0 && (
                  <section className="border-b py-1">
                    <h6 className="mb-3 px-9 py-1 whitespace-nowrap uppercase">
                      {t("recentlyViewedProducts")}
                    </h6>
                    {autocomplete.data.recentlyViewed.map((item) => (
                      <SuggestedProduct
                        key={item.key}
                        productGroup={item}
                        market={market}
                        query={searchQuery}
                        dataType="recent-product"
                      />
                    ))}
                  </section>
                )}
              </Search.List>
            </>
          )}
        </>
      ) : null}
    </Search.Root>
  );
};

export default function HeaderSearchWithSuspense(
  props: ComponentProps<typeof HeaderSearch>,
) {
  return (
    <Suspense
      fallback={
        <div
          className={cn(
            "group relative h-10 w-full bg-white lg:relative",
            props.classNames?.root,
          )}
        />
      }
    >
      <HeaderSearch {...props} />
    </Suspense>
  );
}
